import React, { Component } from 'react';
import { action, makeObservable, observable, when, type IReactionDisposer } from 'mobx';
import { observer } from 'mobx-react';

import type { ChatResponseItem } from '@root/models';
import { modalService } from '@components/Modal';
import ContextMenu, { ContextMenuItem, ContextMenuSeparator } from '@components/ContextMenu/index.ts';
import SearchChatDialog from './SearchChatDialog';
import { ImgNewChat } from '@images/ImgNewChat';
import { ImgSearch } from '@images/ImgSearch';
import { ImgStar } from '@components/Images/ImgStar';
import { ImgClock } from '@components/Images/ImgClock';
import { ImgMenuMoreHorizontal } from '@components/Images/ImgThreeDots';
import { ImgArchive } from '@components/Images/ImgArchive';
import { ImgImage } from '@components/Images/ImgImage';
import TypewriterText from '@components/TypewriterText';
import { userService } from '@services/userService';
import { ImgDown } from '@components/Images/ImgDown';
import { ImgChats } from '@components/Images/ImgChats';
import { ImgMenu } from '@components/Images/ImgMenu';

type SidebarProps = {
  chats: ChatResponseItem[];
  archivedChats: ChatResponseItem[];
  activeChatId: string | null;
  generatingChatIds?: Set<string>;
  onToggle: (isVisible: boolean) => void;
  onSelect: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
  onShareChat: (chatId: string) => void;
  onFavoriteChat: (chatId: string, isFavorite: boolean) => void;
  onArchiveChat: (chatId: string) => void;
  onUnarchiveChat: (chatId: string) => void;
  onRenameChat: (chatId: string, currentName: string) => void;
  onRegenerateChat: (chatId: string) => void;
  onScheduleChat: (chatId: string) => void;
  onShowGallery: () => void;
  showGallery?: boolean;
}

@observer
export default class Sidebar extends Component<SidebarProps> {
  @observable private _chatListDivRef = React.createRef<HTMLDivElement>();
  @observable isSidebarOpen: boolean = window.innerWidth >= 768;
  @observable displayedChatsCount: number = 200;
  @observable isFavoritesSectionCollapsed: boolean = localStorage.getItem('sidebar_favorites_section_collapsed') === 'true';
  @observable isChatsCollapsed: boolean = localStorage.getItem('sidebar_chats_collapsed') === 'true';
  @observable isArchivedSectionCollapsed: boolean = localStorage.getItem('sidebar_archived_section_collapsed') === 'true';
  private readonly PAGE_SIZE = 200;
  private listObserverDisposer: IReactionDisposer | null = null;

  constructor(props: SidebarProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this._onDocumentKeyDown);
    document.addEventListener('click', this._onDocumentClickOutside);

    this.listObserverDisposer = when(() => !!this._chatListDivRef.current, () => {
      const chatList = this._chatListDivRef.current;
      if (chatList) {
        chatList.addEventListener('scroll', this._onChatListScroll);
      }
    });
  }

  @action
  componentDidUpdate(prevProps: SidebarProps) {
    if (prevProps.chats.length !== this.props.chats.length) {
      this.displayedChatsCount = Math.min(this.PAGE_SIZE, this.props.chats.length);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this._onDocumentKeyDown);
    document.removeEventListener('click', this._onDocumentClickOutside);

    const chatList = this._chatListDivRef.current;
    if (chatList) {
      chatList.removeEventListener('scroll', this._onChatListScroll);
    }

    if (this.listObserverDisposer) {
      this.listObserverDisposer();
      this.listObserverDisposer = null;
    }
  }

  private _groupChatsByTime(chats: ChatResponseItem[]): Array<{ label: string; chats: ChatResponseItem[] }> {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const sevenDaysAgoStart = new Date(todayStart);
    sevenDaysAgoStart.setDate(sevenDaysAgoStart.getDate() - 7);

    const groups = new Map<string, ChatResponseItem[]>();
    const groupOrder: string[] = [];

    const addToGroup = (label: string, chat: ChatResponseItem) => {
      if (!groups.has(label)) {
        groups.set(label, []);
        groupOrder.push(label);
      }
      groups.get(label)!.push(chat);
    };

    for (const chat of chats) {
      const d = new Date(chat.modifiedAt);
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      if (dayStart >= todayStart) {
        addToGroup('Today', chat);
      } else if (dayStart >= yesterdayStart) {
        addToGroup('Yesterday', chat);
      } else if (dayStart >= sevenDaysAgoStart) {
        addToGroup('Last 7 Days', chat);
      } else {
        const label = d.getFullYear() === now.getFullYear()
          ? d.toLocaleString('en-US', { month: 'long' })
          : d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        addToGroup(label, chat);
      }
    }

    return groupOrder.map(label => ({ label, chats: groups.get(label)! }));
  }

  private _renderChatItem(chat: ChatResponseItem, isArchived: boolean = false) {
    const { activeChatId, generatingChatIds, onSelect } = this.props;
    return (
      <div
        className={`chat-item ${chat.id === activeChatId ? 'active' : ''} ${generatingChatIds?.has(chat.id) ? 'chat-item--generating' : ''} ${chat.hasUnreadMessages ? 'unread' : ''}`}
        key={chat.id}
        onClick={() => onSelect(chat.id)}
        onMouseDown={(e) => { if (e.button === 1) {
          e.preventDefault();
          window.open(`/c/${chat.id}`, '_blank', 'noopener,noreferrer');
        }}}
      >
        {chat.isFavorite && !isArchived && (
          <span className="chat-item-favorite-badge" title="Favorite"><ImgStar /></span>
        )}
        {chat.isScheduled && !isArchived && (
          <span className="chat-item-schedule-badge" title="Scheduled"><ImgClock /></span>
        )}
        <span className={`chat-item-text${chat.hasUnreadMessages ? ' chat-item-text--unread' : ''}`} title={chat.name}>
          <TypewriterText text={chat.name} className="typewriter-text" />
        </span>
        {/* data-chat-menu marks this button as the ContextMenu trigger */}
        <button
          className="chat-item-menu-btn"
          title="Options"
          data-chat-menu="1"
          data-chat-id={chat.id}
          data-chat-name={chat.name}
          data-archived={isArchived ? 'true' : 'false'}
          data-favorite={chat.isFavorite ? 'true' : 'false'}
          data-schedule-enabled={chat.isScheduled ? 'true' : 'false'}
          data-cron={chat.cron || ''}
          onClick={(e) => e.stopPropagation()}
        >
          <ImgMenuMoreHorizontal />
        </button>
      </div>
    );
  }

  render() {
    const { chats, archivedChats, showGallery, onNewChat, onShowGallery } = this.props;
    const displayedChats = chats.slice(0, this.displayedChatsCount);
    const favoriteChats = displayedChats.filter(c => c.isFavorite);
    const regularChats = displayedChats.filter(c => !c.isFavorite);

    return (
      <>
        {(!!chats.length || archivedChats.length > 0) && (
          <div className={`sidebar ${this.isSidebarOpen ? '' : 'collapsed'}`}>
            <div className="sidebar-header">
              <button className="burger-menu" onClick={this._onToggleSidebarClick}><ImgMenu size={20} /></button>
              <button className="search-menu" onClick={this._onSearchClick}>
                <ImgSearch />
              </button>
              <div className="header-actions">
                <button className="header-btn header-btn-right header-btn-with-text" onClick={onNewChat}>
                  <ImgNewChat />
                  <span className="header-btn-text">New Chat</span>
                </button>
              </div>
            </div>

            <div className="chat-list" ref={this._chatListDivRef}>
              <button className={`sidebar-nav-item${showGallery ? ' sidebar-nav-item-active' : ''}`} onClick={onShowGallery}>
                <ImgImage size={16} />
                <span>Gallery</span>
              </button>
              {userService.showArchive && archivedChats.length > 0 && (
                <>
                  <button className="sidebar-nav-item sidebar-nav-item-collapsible" onClick={this._onToggleArchivedSection}>
                    <ImgArchive size={16} />
                    <span>Archive</span>
                    <span className={`sidebar-nav-collapse-icon${this.isArchivedSectionCollapsed ? ' collapsed' : ''}`}><ImgDown /></span>
                  </button>
                  <div className={`collapsible-section${this.isArchivedSectionCollapsed ? ' collapsible-section--collapsed' : ''}`}>
                    <div>{archivedChats.map(chat => this._renderChatItem(chat, true))}</div>
                  </div>
                </>
              )}
              {favoriteChats.length > 0 && (
                <>
                  <button className="sidebar-nav-item sidebar-nav-item-collapsible" onClick={this._onToggleFavoritesSection}>
                    <ImgStar size={16} />
                    <span>Favorites</span>
                    <span className={`sidebar-nav-collapse-icon${this.isFavoritesSectionCollapsed ? ' collapsed' : ''}`}><ImgDown /></span>
                  </button>
                  <div className={`collapsible-section${this.isFavoritesSectionCollapsed ? ' collapsible-section--collapsed' : ''}`}>
                    <div>{favoriteChats.map(chat => this._renderChatItem(chat))}</div>
                  </div>
                </>
              )}
              <button className="sidebar-nav-item sidebar-nav-item-collapsible" onClick={this._onToggleChats}>
                <ImgChats size={16} />
                <span>Chats</span>
                <span className={`sidebar-nav-collapse-icon${this.isChatsCollapsed ? ' collapsed' : ''}`}><ImgDown /></span>
              </button>
              <div className={`collapsible-section${this.isChatsCollapsed ? ' collapsible-section--collapsed' : ''}`}>
                <div>
                  {this._groupChatsByTime(regularChats).map((group, i) => (
                    <React.Fragment key={group.label}>
                      <div className={`chat-time-label${i === 0 ? ' chat-time-label--first' : ''}`}>{group.label}</div>
                      {group.chats.map(chat => this._renderChatItem(chat))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>


          </div>
        )}

        <ContextMenu selector="[data-chat-menu]">
          {(el) => {
            const chatId = el.dataset.chatId as string;
            const isArchived = el.dataset.archived === 'true';
            const isFavorite = el.dataset.favorite === 'true';
            const chatName = el.dataset.chatName as string;
            return (
              <>
                <ContextMenuItem
                  title="Rename"
                  onClick={() => this.props.onRenameChat(chatId, chatName)}
                />
                <ContextMenuItem
                  title="Regenerate Name"
                  onClick={() => this.props.onRegenerateChat(chatId)}
                />
                <ContextMenuSeparator />
                <ContextMenuItem
                  title="Share"
                  onClick={() => this.props.onShareChat(chatId)}
                />
                {!isArchived && isFavorite && (
                  <ContextMenuItem
                    title="Schedule"
                    onClick={() => this.props.onScheduleChat(chatId)}
                  />
                )}
                {!isArchived && (
                  <ContextMenuItem
                    title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                    onClick={() => this.props.onFavoriteChat(chatId, !isFavorite)}
                  />
                )}
                <ContextMenuSeparator />
                {isArchived ? (
                  <ContextMenuItem
                    title="Unarchive"
                    onClick={() => this.props.onUnarchiveChat(chatId)}
                  />
                ) : (
                  <ContextMenuItem
                    title="Archive"
                    onClick={() => this.props.onArchiveChat(chatId)}
                  />
                )}
                <ContextMenuSeparator />
                <ContextMenuItem
                  title="Delete"
                  className="danger"
                  onClick={() => this.props.onDeleteChat(chatId)}
                />
              </>
            );
          }}
        </ContextMenu>
      </>
    );
  }

  @action
  private _onDocumentKeyDown = (event: KeyboardEvent) => {
    if (this.isSidebarOpen && event.key === 'Escape' && window.innerWidth < 768) {
      this.isSidebarOpen = false;
      this.props.onToggle(this.isSidebarOpen);
    }
  };

  @action
  private _onChatListScroll = (event: Event) => {
    const chatList = event.target as HTMLElement;
    const scrollTop = chatList.scrollTop;
    const scrollHeight = chatList.scrollHeight;
    const clientHeight = chatList.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      const { chats } = this.props;
      if (this.displayedChatsCount < chats.length) {
        this.displayedChatsCount = Math.min(
          this.displayedChatsCount + this.PAGE_SIZE,
          chats.length
        );
      }
    }
  };

  @action
  private _onDocumentClickOutside = (event: MouseEvent) => {
    if (!this.isSidebarOpen || window.innerWidth >= 768) {
      return;
    }

    const target = event.target as HTMLElement;
    if (this.isSidebarOpen
      && !target.closest('.sidebar')
      && !target.closest('.burger-menu')
      && !target.closest('.modal')
      && !target.closest('.contextMenu')
      && !target.classList.contains('archived-back-btn')) {
      this.isSidebarOpen = false;
      this.props.onToggle(this.isSidebarOpen);
    }
  };

  @action
  private _onToggleSidebarClick = () => {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.props.onToggle(this.isSidebarOpen);
  };

  @action
  public close = () => {
    this.isSidebarOpen = false;
    this.props.onToggle(this.isSidebarOpen);
  };

  @action
  public toggle = () => {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.props.onToggle(this.isSidebarOpen);
  };

  @action
  public search = async () => {
    if (window.innerWidth < 768) {
      this.close();
    }

    await modalService.show(SearchChatDialog, {
      chats: this.props.chats,
      onSelectedChat: (chatId: string) => {
        this.props.onSelect(chatId);
      },
      onClose: () => this.close()
    }, { closeByEscape: true, closeOnClickOutside: true });
  };

  @action
  private _onToggleFavoritesSection = () => {
    this.isFavoritesSectionCollapsed = !this.isFavoritesSectionCollapsed;
    localStorage.setItem('sidebar_favorites_section_collapsed', String(this.isFavoritesSectionCollapsed));
  };

  @action
  private _onToggleArchivedSection = () => {
    this.isArchivedSectionCollapsed = !this.isArchivedSectionCollapsed;
    localStorage.setItem('sidebar_archived_section_collapsed', String(this.isArchivedSectionCollapsed));
  };

  @action
  private _onToggleChats = () => {
    this.isChatsCollapsed = !this.isChatsCollapsed;
    localStorage.setItem('sidebar_chats_collapsed', String(this.isChatsCollapsed));
  };

  @action
  private _onSearchClick = async () => {
    await this.search();
  };
}
